import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product SKU.
 * This component listens for variant update events and updates the SKU display accordingly.
 * It handles SKU updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer - The container element for the SKU
 * @property {HTMLElement} sku - The span element that displays the SKU text
 *
 * @extends {Component<Refs>}
 */
class ProductSkuComponent extends Component {
  requiredRefs = ['skuContainer', 'sku'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('.shopify-section, dialog, product-card');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.updateSku);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('.shopify-section, dialog, product-card');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.updateSku);
  }

  /**
   * Resolves the SKU for the selected variant from the section HTML response.
   * @param {Document} html - The fetched section HTML.
   * @returns {string | undefined} The SKU text when found in the response.
   */
  #getSkuFromHtml(html) {
    const productId = this.dataset.productId;
    if (!productId) return undefined;

    const selector = this.dataset.blockId
      ? `product-sku-component[data-block-id="${this.dataset.blockId}"]`
      : `product-sku-component[data-product-id="${productId}"]`;

    const newSkuComponent = html.querySelector(selector);
    const newSku = newSkuComponent?.querySelector('[data-testid="product-sku"]');

    if (!(newSku instanceof HTMLElement)) return undefined;

    return newSku.textContent?.trim() ?? '';
  }

  /**
   * Updates the SKU.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateSku = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    if (!this.refs.sku) return;

    let variantSku = event.detail.data.html ? this.#getSkuFromHtml(event.detail.data.html) : undefined;

    if (variantSku === undefined && event.detail.resource) {
      variantSku = event.detail.resource.sku || '';
    }

    if (variantSku === undefined) return;

    if (variantSku) {
      this.style.display = 'block';
      this.refs.sku.textContent = variantSku;
    } else {
      this.style.display = 'none';
      this.refs.sku.textContent = '';
    }
  };
}

if (!customElements.get('product-sku-component')) {
  customElements.define('product-sku-component', ProductSkuComponent);
}
